import * as React from 'react'
import { useQuery } from 'react-query'
import { Icon } from 'antd-mobile'
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom'
import { getFaqList } from '@feedback/services'
import { IQuestion } from '@feedback/pages/allfb/questionIndex'
import { getAppConfig } from '@feedback/utils/CONST'
import {
  getTextByKey,
  reportFn,
  tracking
} from '@feedback/utils/util'
import VestLayout from '../Layout'
import './index.scss'

export interface VestQuestionListResult {
  faq_type: string
  list: IQuestion[]
}

/**
 * 按类别拉取 FAQ 子集：复用 getFaqList，前端按 faq_type 过滤。
 * 不新增接口；数据形状与既有首页 IQuestionList 一致。
 */
export async function fetchVestQuestionsByType(
  faqType: string
): Promise<VestQuestionListResult> {
  const { code, data, message } = (await getFaqList({ page: 1, limit: 50 })) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message || 'Failed to load FAQ'))
  }

  const list: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.list)
    ? data.list
    : []

  const normalizedType = (faqType || '').trim()
  const matched = list.find(
    (item) => (item?.faq_type || item?.name || '').trim() === normalizedType
  )

  const children: IQuestion[] = Array.isArray(matched?.children)
    ? matched.children
    : Array.isArray(matched?.list)
    ? matched.list
    : []

  return {
    faq_type: matched?.faq_type || normalizedType,
    list: children
      .map((item: any) => ({
        _id: item?._id,
        question: item?.question || item?.name || '',
        star: item?.star ?? 0,
        type_id: item?.type_id ?? 0
      }))
      .filter((item) => item._id && item.question)
  }
}

function parseQuery(search: string): { faq_type: string; type_id?: string } {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  )
  return {
    faq_type: (params.get('faq_type') || '').trim(),
    type_id: params.get('type_id') || undefined
  }
}

interface IProps extends RouteComponentProps {
  [key: string]: any
}

/**
 * 马甲站分类问题列表：对照原型 faq-list 屏。
 * 顶栏返回 + 类别名（Layout nav 模式）；问题逐条列出，点进既有详情页。
 */
const VestQuestionList: React.FC<IProps> = () => {
  const history = useHistory()
  const location = useLocation()
  const { faq_type: faqType } = parseQuery(location.search || '')
  const { appid } = getAppConfig() || {}

  const { data, error, isError, isLoading } = useQuery<
    VestQuestionListResult,
    Error
  >(
    ['vestQuestionList', faqType],
    () => fetchVestQuestionsByType(faqType),
    {
      enabled: Boolean(faqType),
      staleTime: 60 * 1000
    }
  )

  // 类别名即后台 faq_type 原文，不走 i18n
  const title = data?.faq_type || faqType || ''

  const onQuestionClick = (item: IQuestion) => {
    // 埋点：沿用现有首页问题点击上报
    reportFn({ parm1: 1, parm2: 1, parm6: item._id })
    if (appid === 'cm1006') {
      tracking({
        _step_id: '',
        _feedback_page_id: 1,
        _postion_id: 2,
        _search_words: '',
        _faq_id: item._id,
        _src_faq_id: ''
      })
    }
    history.push(`/questionDetail?faq_id=${item._id}`)
  }

  const renderBody = () => {
    if (!faqType) {
      return (
        <div className='vest-qlist__status'>
          {getTextByKey('Nothing more to load')}
        </div>
      )
    }

    if (isLoading) {
      return (
        <div className='vest-qlist__status'>
          <Icon type='loading' />
        </div>
      )
    }

    if (isError) {
      return (
        <div className='vest-qlist__status'>
          {error?.message || getTextByKey('Nothing more to load')}
        </div>
      )
    }

    if (!data?.list?.length) {
      return (
        <div className='vest-qlist__status'>
          {getTextByKey('Nothing more to load')}
        </div>
      )
    }

    return (
      <div className='vest-qlist__list' role='list'>
        {data.list.map((item) => (
          <button
            key={item._id}
            type='button'
            className='vest-qlist__item'
            role='listitem'
            onClick={() => onQuestionClick(item)}
          >
            <span className='vest-qlist__text'>{item.question}</span>
            <i className='vest-qlist__chevron' aria-hidden='true' />
          </button>
        ))}
      </div>
    )
  }

  return (
    <VestLayout
      title={title}
      showBack
      showBottomBar={false}
      bodyClassName='vest-qlist-body'
    >
      <div className='vest-qlist'>
        {/* 内容区再显一次类别名，对齐原型 faq-list-page h1 */}
        {faqType ? <h2 className='vest-qlist__heading'>{title}</h2> : null}
        {renderBody()}
      </div>
    </VestLayout>
  )
}

export default VestQuestionList
